import axios from 'axios';
import { toast } from '@/utils/toast';

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'csrftoken';      // Django defaults
axios.defaults.xsrfHeaderName = 'X-CSRFToken';    // Django expects this
axios.defaults.timeout = parseInt(process.env.VUE_APP_AXIOS_TIMEOUT, 10) || 300000;

export const LOGIN_URL = '/accounts/microsoft/login/';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function applyCsrfDefaults(token) {
  axios.defaults.headers.post['X-CSRFToken'] = token;
  axios.defaults.headers.put['X-CSRFToken'] = token;
  axios.defaults.headers.patch['X-CSRFToken'] = token;
  axios.defaults.headers.delete['X-CSRFToken'] = token;
}

export async function initCsrf() {
  try {
    const res = await axios.get('/api/get-csrf-token'); // your Django URL (no trailing slash)
    const token = getCookie('csrftoken') || (res.data && res.data.csrfToken);
    if (token) applyCsrfDefaults(token);
  } catch (e) {
    console.error('Failed to set CSRF cookie:', e);
  }
}

/**
 * Current CSRF token for requests made outside axios (fetch/SSE). The cookie
 * is the source of truth (Django rotates it on login); falls back to a
 * round-trip only when it is missing.
 */
export async function getCsrfToken() {
  const cookie = getCookie('csrftoken');
  if (cookie) return cookie;
  const res = await axios.get('/api/get-csrf-token');
  const token = res.data && res.data.csrfToken;
  if (token) applyCsrfDefaults(token);
  return token || '';
}

// ---- Session expiry ---------------------------------------------------------
// Only armed once the app has confirmed a logged-in user, so the boot-time
// auth probe and the login screen never raise it.
let sessionWatch = false;

export function watchSession(enabled = true) {
  sessionWatch = enabled;
}

/**
 * 401 always means the session is gone. DRF's SessionAuthentication answers
 * unauthenticated requests with 403 + "Authentication credentials were not
 * provided", which must be told apart from real permission / CSRF denials.
 */
export function isAuthFailure(status, data) {
  if (status === 401) return true;
  if (status !== 403) return false;
  const detail = String((data && (data.detail || data.error)) || (typeof data === 'string' ? data : '')).toLowerCase();
  return detail.includes('authentication credentials') || detail.includes('not authenticated');
}

/** A response that followed a redirect to the login page instead of the API. */
export function isLoginRedirect(responseUrl) {
  return typeof responseUrl === 'string' && /\/accounts\/(microsoft\/)?login/.test(responseUrl);
}

/**
 * The sensitive-data guard refused the text (company policy `block`):
 * 400 `{detail, error_code:'PII_BLOCKED', findings}` from JSON endpoints, or
 * the same payload in an SSE `error` event. Not a session or permission issue.
 */
export function isPiiBlocked(status, data) {
  return (status == null || status === 400) && !!data && typeof data === 'object'
    && data.error_code === 'PII_BLOCKED';
}

export function notifySessionExpired() {
  if (!sessionWatch) return;
  toast.show({
    key: 'session-expired',
    kind: 'error',
    message: 'Your session expired',
    duration: Infinity,
    action: {
      label: 'Sign in',
      onClick: () => { window.location.href = LOGIN_URL; },
    },
  });
}

axios.interceptors.response.use(
  (response) => {
    const url = response.request && response.request.responseURL;
    if (isLoginRedirect(url)) notifySessionExpired();
    return response;
  },
  (error) => {
    const res = error && error.response;
    if (res && isAuthFailure(res.status, res.data)) notifySessionExpired();
    return Promise.reject(error);
  }
);

export default axios;
